import React, { useState } from 'react';
import { css } from '@emotion/react';
import { useMutation, useQuery } from 'urql';

import { fieldsToView, FIELD_TO_VIEW, getExternalUrl } from 'utils/utils';
import {
	MUTATION_UPSERT_REQUEST_PROPERTY_VALUE,
	QUERY_BOOKER_REQUEST_DATA,
} from 'queries/queries-booker';
import { format } from 'date-fns';
import { IconState } from 'components/ProcessStep';
import { theme } from 'styles/theme';
import { Input } from 'components/Input';
import { Button } from 'components/Button';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'components/Tooltip';
import { TooltipDirection } from 'components/Tooltip';
import { IconPlus } from 'icons/Plus';
import { IconExternal } from 'icons/icon-external';
import { ButtonLink } from 'components/ButtonLink';
import { IconEdit } from 'icons/icon-edit';
import { IconCross } from 'icons/icon-cross';
import { SpinnerBlock } from 'components/SpinnerBlock';
import { SummaryPrice } from './SummaryPrice';
import { ScrollBarStyle } from 'styles/ScrollBarStyle';
import { LinkExternal } from 'components/LinkExternal';

import { IconCopy } from 'icons/icon-copy';
import { SummarySeparator } from 'components/SummarySeparator';
import { Strong } from 'components/Strong';
import { CopyToClipboard } from '../components/CopyToClipboard';
import slugify from '@sindresorhus/slugify';

export const RequestDetailDrawer = ({ onClose, url }) => {
	const [requestDetail, executeRequestDetail] = useQuery({
		query: QUERY_BOOKER_REQUEST_DATA,
		variables: { url: url },
	});

	const { fetching, data, error } = requestDetail;

	return (
		<div
			css={css`
				position: fixed;
				top: 0;
				right: 0;
				background: #fff;
				height: 100%;
				box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
				width: 720px;
				display: flex;
				flex-direction: column;
				z-index: 9900;
			`}
		>
			<div
				css={css`
					display: flex;
					justify-content: space-between;
					padding: 16px 16px 16px 32px;
					border-bottom: 1px solid #f0f0ff;
				`}
			>
				<div>
					{!fetching && (
						<>
							<div
								css={css`
									display: flex;
									align-items: center;
									gap: 12px;
									margin-bottom: 12px;
								`}
							>
								<img
									src={
										process.env.ASSETS_URL +
										'artists/' +
										slugify(data?.requestDetail?.artist?.url) +
										'.png'
									}
									css={css`
										border-radius: 12px;
										width: 32px;
										flex-shrink: 0;
									`}
								/>

								<h3
									css={css`
										margin-bottom: 0;
									`}
								>
									{data.requestDetail?.artist?.name}
								</h3>
							</div>
							<div>
								<strong>{data.requestDetail.event_name ?? 'Název akce bude doplněn'}</strong> ·{' '}
								{data.requestDetail.event_address_place} · {data.requestDetail.event_address_city} ·{' '}
								{format(new Date(data.requestDetail.event_date), 'dd. MM. YYY')} ·{' '}
								{data.requestDetail.time_performance ? (
									<>
										{format(new Date('2020-01-01 ' + data.requestDetail.time_performance), 'HH:mm')}
									</>
								) : (
									<em
										css={css`
											color: #e69500;
										`}
									>
										Čas konání bude upřesněn
									</em>
								)}{' '}
							</div>
							<div
								css={css`
									font-size: 13px;
									color: ${theme.color.text['300']};
								`}
							>
								{/* <div>{data.requestDetail.id}</div> */}
								<div>
									Posláno na <Strong>{data.requestDetail.main_email}</Strong>
								</div>
							</div>
						</>
					)}
				</div>

				<div
					css={css`
						display: flex;
					`}
				>
					{!fetching && (
						<>
							<div>
								<Tooltip content="Otevřít v novém okně" direction={TooltipDirection.BOTTOM}>
									<ButtonLink
										small
										ghost
										external
										square
										to={process.env.BASE_URL + 'booking/' + data.requestDetail.id + '/'}
									>
										<IconExternal />
									</ButtonLink>
								</Tooltip>
							</div>
							<div>
								<CopyToClipboard
									ghost
									small
									value={process.env.BASE_URL + 'booking/' + data.requestDetail.id + '/'}
									square
									tooltipDirection={TooltipDirection.LEFT}
								>
									<IconCopy />
								</CopyToClipboard>
							</div>
						</>
					)}

					<Button small square ghost onClick={onClose}>
						<IconCross />
					</Button>
				</div>
			</div>

			<div
				css={[
					css`
						height: 100%;
						overflow: auto;
					`,
					ScrollBarStyle(),
				]}
			>
				<div
					css={css`
						display: flex;
						justify-content: space-between;
						padding: 16px 32px;
					`}
				>
					{!!fetching && (
						<div
							css={css`
								width: 100%;
							`}
						>
							<SpinnerBlock />
						</div>
					)}
					{!fetching && (
						<div
							css={css`
								width: 100%;
							`}
						>
							<div
								css={css`
									padding: 0 0 8px;
									color: ${theme.color.primary['400']};
								`}
							>
								Dokumenty (odkazy)
							</div>

							<div
								css={css`
									width: 100%;
									margin-bottom: 24px;
								`}
							>
								<EditableProperty
									id={url}
									label="Smlouva"
									property="link_contract"
									value={data.requestDetail.link_contract}
									tooltipAdd="Přidat"
									tooltipEdit="Upravit"
								/>
								<EditableProperty
									id={url}
									label="Faktura"
									property="link_invoice"
									value={data.requestDetail.link_invoice}
									tooltipAdd="Přidat"
									tooltipEdit="Upravit"
								/>
							</div>

							{fieldsToView.map((section) => {
								return <DetailSection section={section} data={data.requestDetail} />;
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const DetailSection = ({ section, data }) => {
	return (
		<div
			css={css`
				width: 100%;
				margin-bottom: 20px;
			`}
		>
			<SummarySeparator />

			<div
				css={css`
					padding: 0 0 8px;
					color: ${theme.color.primary['400']};
				`}
			>
				{section.title}
			</div>

			{data[section.updateBadge] && (
				<div
					css={css`
						margin-bottom: 12px;
						font-size: 13px;
						line-height: 20px;
						color: ${theme.color.text['300']};
					`}
				>
					<strong
						css={css`
							font-weight: 500;
						`}
					>
						Poslední aktualizace:
					</strong>{' '}
					{format(new Date(data[section.updateBadge]), 'dd. MM. YYY, HH:mm')}
				</div>
			)}

			<div>
				{section.fields.map(
					({ key, name, type, enumOptions, showVATCalculation, dependsOn }, index) => {
						// kdyz je nastavena dependency
						if (!!dependsOn) {
							// console.log('here is dependency');

							// a value je odlisna od dependecny value -> nezobrazovat
							if (data[dependsOn.property] !== dependsOn.value) {
								return;
							}
						}

						return (
							<div
								key={key}
								css={css`
									display: flex;
									justify-content: space-between;
									padding: 4px 0;

									&:hover {
										background: ${theme.color.primary['50']};
									}
								`}
							>
								<div
									css={css`
										color: ${theme.color.text['300']};
										margin-right: 24px;
									`}
								>
									{name}
								</div>
								<div
									css={css`
										word-wrap: break-word;
										max-width: 65%;
										text-align: right;
									`}
								>
									{data[key] ||
									type === FIELD_TO_VIEW.BOOLEAN ||
									type === FIELD_TO_VIEW.PRICE ||
									(type === FIELD_TO_VIEW.DISTANCE && data[key] !== null) ? (
										<>
											{type === FIELD_TO_VIEW.BOOLEAN && <>{data[key] ? 'Ano' : 'Ne'}</>}
											{type === FIELD_TO_VIEW.DATE && (
												<>{format(new Date(data[key]), 'dd. MM. YYY')}</>
											)}
											{type === FIELD_TO_VIEW.ENUM && <>{enumOptions[data[key]]}</>}
											{type === FIELD_TO_VIEW.DISTANCE && <>{data[key]} km</>}
											{type === FIELD_TO_VIEW.PRICE && (
												<SummaryPrice
													value={data[key]}
													isVAT={data.fee_vat}
													showVATCalculation={showVATCalculation}
													alignRight
												/>
											)}
											{type === FIELD_TO_VIEW.LINK && (
												<>
													<LinkExternal href={data[key]}>{data[key]}</LinkExternal>
												</>
											)}
											{type === FIELD_TO_VIEW.STRING && <>{data[key]}</>}
											{type === FIELD_TO_VIEW.EMAIL && (
												<LinkExternal href={'mailto:' + data[key]}>{data[key]}</LinkExternal>
											)}
											{type === FIELD_TO_VIEW.TIME && (
												<>{format(new Date('2020-01-01 ' + data[key]), 'HH:mm')}</>
											)}
										</>
									) : (
										<div
											css={css`
												display: flex;
												align-items: center;
											`}
										>
											<IconState isIncomplete sizeSmall />

											<strong
												css={css`
													font-weight: 600;
													padding-left: 8px;
													color: #e69500;
												`}
											>
												Nutno doplnit
											</strong>
										</div>
									)}
								</div>
							</div>
						);
					},
				)}
			</div>
		</div>
	);
};

const EditableProperty = ({
	label,
	id,
	property,
	value,
	tooltipAdd,
	tooltipEdit,
}: {
	label: string;
	id: string;
	property: string;
	value: string;
	tooltipAdd?: string;
	tooltipEdit?: string;
}) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const [updatePropertyValue, executerUpdatePropertyValue] = useMutation(
		MUTATION_UPSERT_REQUEST_PROPERTY_VALUE,
	);

	const { handleSubmit, register } = useForm({
		defaultValues: {
			value: value,
		},
	});

	const onSubmit = (data) => {
		// console.log(data);

		executerUpdatePropertyValue({
			id: id,
			name: property,
			value: data.value,
		}).then((result) => {
			if (result?.error) {
			} else {
				setIsEditing(false);
			}
		});
	};

	return (
		<div
			css={css`
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 4px 0;

				&:hover {
					background: ${theme.color.primary['50']};
				}
			`}
		>
			<div
				css={css`
					color: ${theme.color.text['300']};
				`}
			>
				{label}
			</div>

			{isEditing ? (
				<form
					onSubmit={handleSubmit(onSubmit)}
					css={css`
						display: flex;
					`}
				>
					<Input sizeMedium width={400} {...register('value')} placeholder="https://" />

					<div
						css={css`
							margin-left: 8px;
						`}
					>
						<Button small type="submit" isProcessing={updatePropertyValue.fetching}>
							Uložit
						</Button>
					</div>
				</form>
			) : (
				<div>
					{!!value ? (
						<div
							css={css`
								display: flex;
								align-items: center;
							`}
						>
							<span
								css={css`
									margin-right: 4px;
								`}
							>
								<Tooltip content={value} direction={TooltipDirection.LEFT}>
									<ButtonLink to={getExternalUrl(value)} external small square ghost>
										<IconExternal />
									</ButtonLink>
								</Tooltip>
							</span>

							<Tooltip content={tooltipEdit} direction={TooltipDirection.TOP}>
								<Button small square ghost onClick={() => setIsEditing(true)}>
									<IconEdit />
								</Button>
							</Tooltip>
						</div>
					) : (
						<Tooltip content={tooltipAdd} direction={TooltipDirection.TOP}>
							<Button small square ghostPrimary onClick={() => setIsEditing(true)}>
								<IconPlus />
							</Button>
						</Tooltip>
					)}
				</div>
			)}
		</div>
	);
};
